import * as THREE from 'three';
import type { PlayerState, Role } from '@shared/types';
import type { GameAssets } from '../game/AssetLoader';
import { Interpolator } from './Interpolator';

// ============================================================
// Remote player entry
// ============================================================

export interface RemoteEntry {
  readonly mesh: THREE.Group;
  readonly role: Role;
  readonly interpolator: Interpolator;
  readonly username: string;
}

// ============================================================
// Callbacks for integration with game systems
// ============================================================

export interface RemotePlayerCallbacks {
  readonly onSpawn: (id: string, entry: RemoteEntry) => void;
  readonly onRemove: (id: string, entry: RemoteEntry) => void;
}

// ============================================================
// Manages rendering of all remote players
// ============================================================

const STALE_FADE_START = 1000;  // start fading after 1s
const STALE_REMOVE_TIME = 3000; // remove after 3s

export class RemotePlayerManager {
  private readonly remotes = new Map<string, RemoteEntry>();
  private readonly lastUpdateTime = new Map<string, number>();

  // Reusable objects for interpolation output
  private readonly tempPos = new THREE.Vector3();
  private readonly tempEuler = new THREE.Euler();

  constructor(
    private readonly scene: THREE.Scene,
    private readonly assets: GameAssets,
    private readonly localPlayerId: string,
    private readonly callbacks?: RemotePlayerCallbacks,
  ) {}

  /**
   * Called each server tick with all player states.
   * Creates new meshes, updates existing, removes disconnected.
   */
  onRemoteStates(states: Record<string, PlayerState>): void {
    const now = performance.now();

    // Update or create entries
    for (const [id, state] of Object.entries(states)) {
      // Skip local player
      if (id === this.localPlayerId) continue;

      const existing = this.remotes.get(id);

      if (existing) {
        // Push new snapshot into interpolator
        existing.interpolator.pushSnapshot(
          state.position,
          state.rotation,
          now,
          state.velocity,
        );

        // Toggle visibility based on alive state
        existing.mesh.visible = state.alive;
        this.lastUpdateTime.set(id, now);

        // Restore opacity if player was fading
        this.setMeshOpacity(existing.mesh, 1);
      } else {
        // New remote player — spawn mesh
        this.spawnRemote(id, state, now);
      }
    }

    // Remove players no longer in state
    const toRemove: [string, RemoteEntry][] = [];
    for (const [id, entry] of this.remotes) {
      if (!(id in states) || id === this.localPlayerId) {
        toRemove.push([id, entry]);
      }
    }
    for (const [id, entry] of toRemove) {
      this.removeRemote(id, entry);
    }
  }

  /**
   * Called every render frame to interpolate remote positions.
   */
  update(): void {
    const now = performance.now();

    // Check for stale players and handle fade/removal
    const staleRemove: [string, RemoteEntry][] = [];
    for (const [id, entry] of this.remotes) {
      const lastUpdate = this.lastUpdateTime.get(id) ?? now;
      const elapsed = now - lastUpdate;

      if (elapsed >= STALE_REMOVE_TIME) {
        staleRemove.push([id, entry]);
        continue;
      }

      if (elapsed >= STALE_FADE_START) {
        const fadeProgress = (elapsed - STALE_FADE_START) / (STALE_REMOVE_TIME - STALE_FADE_START);
        this.setMeshOpacity(entry.mesh, 1 - fadeProgress);
      }

      if (!entry.mesh.visible) continue;

      const ok = entry.interpolator.getInterpolated(
        now,
        this.tempPos,
        this.tempEuler,
      );

      if (ok) {
        entry.mesh.position.copy(this.tempPos);
        entry.mesh.rotation.copy(this.tempEuler);
      }
    }

    for (const [id, entry] of staleRemove) {
      console.log(`[SKYFALL] Removing stale remote player: ${entry.username}`);
      this.removeRemote(id, entry);
    }
  }

  /**
   * Returns all remote meshes for minimap / marker integration.
   */
  getRemotes(): ReadonlyMap<string, RemoteEntry> {
    return this.remotes;
  }

  /**
   * Remove a specific player (e.g. on disconnect event).
   */
  removeById(id: string): void {
    const entry = this.remotes.get(id);
    if (entry) {
      this.removeRemote(id, entry);
    }
  }

  dispose(): void {
    for (const [id, entry] of this.remotes) {
      this.callbacks?.onRemove(id, entry);
      this.scene.remove(entry.mesh);
      entry.interpolator.clear();
    }
    this.remotes.clear();
  }

  // ── Private ───────────────────────────────────────────────

  private spawnRemote(id: string, state: PlayerState, now: number): void {
    const model = state.role === 'shahed'
      ? this.assets.shahed.clone()
      : this.assets.f16.clone();

    model.name = `remote-${id}`;
    model.position.set(state.position.x, state.position.y, state.position.z);
    model.rotation.set(state.rotation.x, state.rotation.y, state.rotation.z);
    model.visible = state.alive;

    this.scene.add(model);

    const interpolator = new Interpolator();
    interpolator.pushSnapshot(state.position, state.rotation, now, state.velocity);

    const entry: RemoteEntry = {
      mesh: model,
      role: state.role,
      interpolator,
      username: state.username,
    };

    this.remotes.set(id, entry);
    this.lastUpdateTime.set(id, now);
    this.callbacks?.onSpawn(id, entry);

    console.log(`[SKYFALL] Remote player spawned: ${state.username} (${state.role})`);
  }

  private setMeshOpacity(mesh: THREE.Group, opacity: number): void {
    mesh.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        const mat = child.material as THREE.MeshStandardMaterial;
        mat.transparent = true;
        mat.opacity = opacity;
      }
    });
  }

  private removeRemote(id: string, entry: RemoteEntry): void {
    this.callbacks?.onRemove(id, entry);
    this.scene.remove(entry.mesh);
    entry.interpolator.clear();
    this.remotes.delete(id);
    this.lastUpdateTime.delete(id);
    console.log(`[SKYFALL] Remote player removed: ${entry.username}`);
  }
}
